/**
 * Kingz Slide Cart Drawer
 * A lightweight, performant native Shopify cart solution
 * Uses Shopify Ajax Cart API for all operations
 */

class KingzSlideCart {
  constructor() {
    // DOM Elements
    this.drawer = document.getElementById('kingz-slide-cart-drawer');
    this.panel = document.querySelector('.kingz-slide-cart-panel');
    this.overlay = document.getElementById('kingz-cart-overlay');
    this.closeBtn = document.getElementById('kingz-cart-close-btn');
    this.miniCartBtns = Array.from(document.querySelectorAll('[data-kingz-mini-cart-trigger], .kingz-mini-cart-icon'));
    this.miniCartBtn = this.miniCartBtns[0] || null;
    this.miniCartCounts = Array.from(document.querySelectorAll('[data-kingz-mini-cart-count], .kingz-mini-cart-badge'));
    this.miniCartCount = this.miniCartCounts[0] || null;
    this.itemsContainer = document.getElementById('kingz-cart-items-container');
    this.emptyState = document.getElementById('kingz-cart-empty-state');
    this.footer = document.getElementById('kingz-cart-footer');
    this.subtotal = document.getElementById('kingz-cart-subtotal');
    this.continueShopping = document.getElementById('kingz-empty-continue-btn');
    this.viewCartBtn = document.getElementById('kingz-view-cart-btn');
    this.checkoutBtn = document.getElementById('kingz-checkout-btn');

    // Shopify storefront root, including locale-aware roots when present.
    this.routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    // State
    this.isOpen = false;
    this.isUpdating = false;
    this.cart = null;

    // Debounce timers
    this.updateDebounce = null;

    // Initialize
    this.init();
  }

  /**
   * Initialize event listeners and fetch initial cart
   */
  init() {
    // Open/Close handlers
    this.miniCartBtns.forEach((btn) => btn.addEventListener('click', (event) => { event.preventDefault(); this.toggle(); }));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.continueShopping?.addEventListener('click', () => {
      this.close();
      // Navigate to first collection or shop page
      window.location.href = this.buildRoute('collections/all');
    });

    // Re-sync immediately before leaving the drawer so the cart page/checkout
    // always use Shopify's real cart session, not stale drawer state.
    this.viewCartBtn?.addEventListener('click', (event) => this.handleCartNavigation(event, 'cart'));
    this.checkoutBtn?.addEventListener('click', (event) => this.handleCartNavigation(event, 'checkout'));

    // Keyboard handling
    document.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Fetch initial cart
    this.fetchCart();

    // Listen for add-to-cart events (other parts of the page)
    document.addEventListener('kingz:cart-updated', () => this.fetchCart());

    // Intercept standard Shopify add-to-cart forms for drawer UX in the working theme preview.
    this.bindAddToCartForms();

    // Prevent scrolling when drawer is open
    this.drawer?.addEventListener('wheel', (e) => {
      if (this.isOpen && e.target.closest('.kingz-slide-cart-overlay')) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  /**
   * Intercept native product add-to-cart forms and open drawer after successful Ajax add.
   */
  bindAddToCartForms() {
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

      event.preventDefault();
      // The working theme still has its own Ajax cart feature enabled. Once the
      // Kingz drawer owns this form submission, stop any later theme/app handler
      // from also mutating UI state or showing a cart that did not come from
      // Shopify's persisted Ajax cart response.
      event.stopImmediatePropagation();
      const submitButton = form.querySelector('[type="submit"], button:not([type])');
      const originalDisabled = submitButton?.disabled;
      if (submitButton) submitButton.disabled = true;

      let addWasAccepted = false;

      try {
        const formData = new FormData(form);
        if (!formData.get('quantity')) formData.set('quantity', '1');

        const response = await fetch(this.buildRoute('cart/add.js'), {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        if (!response.ok) throw new Error('Failed to add product to cart');
        addWasAccepted = true;
        const addResult = await response.json();
        const syncedCart = await this.fetchCart();
        if (!syncedCart || !syncedCart.items || syncedCart.items.length === 0) {
          throw new Error(`Product was added but Shopify cart session did not persist. Add response: ${JSON.stringify(addResult)}`);
        }
        this.open();
        document.dispatchEvent(new CustomEvent('kingz:cart-updated', { detail: { cart: syncedCart, addResult } }));
      } catch (error) {
        console.error('Kingz add-to-cart error:', error);

        // If Shopify already accepted the Ajax add, never submit the native
        // form as a fallback. Doing so can double-add the item and navigate
        // away from the drawer. Instead, re-sync/open the drawer from the
        // persisted Shopify cart session.
        if (addWasAccepted) {
          try {
            await this.fetchCart();
            this.open();
            document.dispatchEvent(new CustomEvent('kingz:cart-updated', { detail: { cart: this.cart } }));
          } catch (syncError) {
            console.error('Kingz post-add cart sync error:', syncError);
          }
        } else {
          HTMLFormElement.prototype.submit.call(form);
        }
      } finally {
        if (submitButton) submitButton.disabled = originalDisabled || false;
      }
    }, true);
  }


  /**
   * Build a Shopify route using the store's locale-aware routes root.
   */
  buildRoute(path) {
    const cleanPath = String(path || '').replace(/^\/+/, '');
    const root = this.routesRoot || '/';
    return `${root}${root.endsWith('/') ? '' : '/'}${cleanPath}`;
  }

  /**
   * Re-fetch the real Shopify cart before navigating out of the drawer.
   * This prevents the cart page from being opened from stale drawer state and
   * gives the Ajax Cart API a final sync point after add/update operations.
   */
  async handleCartNavigation(event, path) {
    event.preventDefault();
    const destination = this.buildRoute(path);

    try {
      await this.fetchCart();
    } catch (error) {
      console.warn('Kingz cart navigation sync failed; continuing to destination:', error);
    }

    window.location.href = destination;
  }

  /**
   * Toggle drawer open/closed
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open drawer
   */
  open() {
    if (this.isOpen || this.isUpdating) return;

    this.isOpen = true;
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.miniCartBtns.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus management - move focus to close button
    this.closeBtn?.focus();

    // Animate open
    this.panel?.offsetHeight; // Trigger reflow
  }

  /**
   * Close drawer
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.drawer?.classList.remove('is-open');
    this.miniCartBtns.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

    // Restore body scroll
    document.body.style.overflow = '';

    // Return focus to mini cart button
    this.miniCartBtn?.focus();
  }

  /**
   * Fetch cart data from Shopify Ajax API
   */
  async fetchCart() {
    try {
      const response = await fetch(this.buildRoute('cart.js'), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch cart');

      this.cart = await response.json();
      this.render();
      return this.cart;
    } catch (error) {
      console.error('Kingz Cart Error:', error);
      this.showError('Failed to load cart');
    }
  }

  /**
   * Update cart line item quantity via Ajax API
   */
  async updateLineItem(lineKey, quantity) {
    if (this.isUpdating) return;

    const preserveDrawerOpen = this.isOpen || this.drawer?.classList.contains('is-open');

    try {
      this.isUpdating = true;
      this.setItemUpdating(lineKey, true);

      const updates = {};
      updates[lineKey] = quantity;

      const response = await fetch(this.buildRoute('cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) throw new Error('Failed to update cart');

      await this.fetchCart();

      // Keep drawer visibly open after quantity/remove Ajax re-render.
      if (preserveDrawerOpen) {
        this.isOpen = true;
        this.drawer?.classList.add('is-open');
        this.miniCartBtns?.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
        document.body.style.overflow = 'hidden';
      }
    } catch (error) {
      console.error('Update error:', error);
      this.showError('Failed to update quantity');
      await this.fetchCart();
      if (preserveDrawerOpen) {
        this.isOpen = true;
        this.drawer?.classList.add('is-open');
        this.miniCartBtns?.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
        document.body.style.overflow = 'hidden';
      }
    } finally {
      this.isUpdating = false;
      this.setItemUpdating(lineKey, false);
    }
  }

  /**
   * Remove item from cart
   */
  async removeItem(lineKey) {
    await this.updateLineItem(lineKey, 0);
  }

  /**
   * Render cart UI
   */
  render() {
    if (!this.cart || !this.itemsContainer) return;

    const items = this.cart.items || [];
    const itemCount = items.length;
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

    // Update mini cart badge and free-shipping progress
    this.updateMiniCartBadge(totalItems);
    this.updateFreeShippingProgress();
    this.updateKitSavingsProgress(totalItems);

    if (itemCount === 0) {
      // Show empty state
      this.itemsContainer.style.display = 'none';
      this.emptyState.style.display = 'flex';
      this.footer.style.display = 'none';
    } else {
      // Render items
      this.itemsContainer.style.display = 'block';
      this.emptyState.style.display = 'none';
      this.footer.style.display = 'flex';

      this.renderItems(items);
      this.updateSubtotal();
    }
  }

  /**
   * Render cart items
   */
  renderItems(items) {
    this.itemsContainer.innerHTML = items
      .map((item) => this.createItemElement(item))
      .join('');

    // Attach event listeners
    this.attachItemListeners();
  }

  /**
   * Create HTML for a single cart item
   */
  createItemElement(item) {
    const variantOptions = item.variant_options
      ?.filter((opt) => opt !== 'Default')
      .map((opt) => `<p class="kingz-cart-item-option"><span class="kingz-cart-item-option-label">${this.escapeHtml(opt)}</span></p>`)
      .join('') || '';

    const discountHtml = item.original_price !== item.final_price
      ? `<span class="kingz-cart-item-original-price">${this.formatMoney(item.original_price)}</span>`
      : '';

    const imageUrl = item.image
      ? `${item.image}&width=100`
      : '';

    const imageHtml = imageUrl
      ? `<img src="${imageUrl}" alt="${this.escapeHtml(item.title)}" width="100" height="100" loading="lazy">`
      : '<div class="kingz-cart-item-no-image">No image</div>';

    return `
      <div class="kingz-cart-item" data-line-item-key="${item.key}" role="listitem">
        <div class="kingz-cart-item-image">
          ${imageHtml}
        </div>
        <div class="kingz-cart-item-details">
          <h3 class="kingz-cart-item-title">
            <a href="${this.escapeHtml(item.url)}">${this.escapeHtml(item.title)}</a>
          </h3>
          ${variantOptions ? `<div class="kingz-cart-item-options">${variantOptions}</div>` : ''}
          <div class="kingz-cart-item-price">
            ${discountHtml}
            <span class="kingz-cart-item-final-price">${this.formatMoney(item.final_price)}</span>
          </div>
        </div>
        <div class="kingz-cart-item-actions">
          <div class="kingz-quantity-control">
            <button class="kingz-quantity-btn kingz-quantity-minus" data-action="decrease" data-line-key="${item.key}" aria-label="Decrease quantity of ${this.escapeHtml(item.title)}" type="button">−</button>
            <input type="number" class="kingz-quantity-input" value="${item.quantity}" min="1" max="99" data-line-key="${item.key}" aria-label="Quantity of ${this.escapeHtml(item.title)}">
            <button class="kingz-quantity-btn kingz-quantity-plus" data-action="increase" data-line-key="${item.key}" aria-label="Increase quantity of ${this.escapeHtml(item.title)}" type="button">+</button>
          </div>
          <button class="kingz-cart-item-remove" data-action="remove" data-line-key="${item.key}" aria-label="Remove ${this.escapeHtml(item.title)}" type="button">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Attach event listeners to rendered items
   */
  attachItemListeners() {
    // Quantity buttons
    this.itemsContainer.querySelectorAll('.kingz-quantity-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleQuantityButton(e));
    });

    // Quantity input
    this.itemsContainer.querySelectorAll('.kingz-quantity-input').forEach((input) => {
      input.addEventListener('change', (e) => this.handleQuantityInput(e));
      input.addEventListener('blur', (e) => this.handleQuantityInput(e));
    });

    // Remove buttons
    this.itemsContainer.querySelectorAll('.kingz-cart-item-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleRemove(e));
    });
  }

  /**
   * Handle quantity button clicks
   */
  handleQuantityButton(event) {
    const btn = event.currentTarget;
    const action = btn.dataset.action;
    const lineKey = btn.dataset.lineKey;
    const input = btn.closest('.kingz-quantity-control').querySelector('.kingz-quantity-input');

    let newQty = parseInt(input.value, 10);

    if (action === 'increase') {
      newQty = Math.min(newQty + 1, 99);
    } else if (action === 'decrease') {
      newQty = Math.max(newQty - 1, 1);
    }

    input.value = newQty;
    this.updateLineItem(lineKey, newQty);
  }

  /**
   * Handle quantity input changes
   */
  handleQuantityInput(event) {
    const input = event.currentTarget;
    let qty = parseInt(input.value, 10);

    // Validate quantity
    if (isNaN(qty) || qty < 1) {
      qty = 1;
    } else if (qty > 99) {
      qty = 99;
    }

    input.value = qty;

    const lineKey = input.dataset.lineKey;
    clearTimeout(this.updateDebounce);
    this.updateDebounce = setTimeout(() => {
      this.updateLineItem(lineKey, qty);
    }, 300);
  }

  /**
   * Handle remove button clicks
   */
  handleRemove(event) {
    const btn = event.currentTarget;
    const lineKey = btn.dataset.lineKey;

    // Remove immediately without a confirmation dialog (matches UK/EU stores).
    this.removeItem(lineKey);
  }

  /**
   * Update mini cart badge with item count
   */
  updateMiniCartBadge(count) {
    if (!this.miniCartCounts || this.miniCartCounts.length === 0) return;

    this.miniCartCounts.forEach((countEl) => {
      countEl.textContent = count;
      countEl.dataset.count = count;
      countEl.setAttribute('aria-label', `${count} items in cart`);
    });
  }


  /**
   * Update free-shipping progress from the real Shopify cart subtotal.
   */
  updateFreeShippingProgress() {
    const container = document.getElementById('kingz-cart-free-shipping');
    const message = document.getElementById('kingz-cart-free-shipping-message');
    const bar = document.getElementById('kingz-cart-free-shipping-bar');
    if (!container || !message || !bar) return;

    const threshold = parseInt(container.dataset.threshold || '12000', 10);
    const subtotal = (this.cart && this.cart.items_subtotal_price) || 0;
    const remaining = Math.max(threshold - subtotal, 0);
    const progress = threshold > 0 ? Math.min((subtotal / threshold) * 100, 100) : 0;

    bar.style.width = `${progress}%`;

    if (subtotal <= 0) {
      message.textContent = `Add ${this.formatMoney(threshold)} to unlock free domestic shipping.`;
    } else if (remaining > 0) {
      message.textContent = `You are ${this.formatMoney(remaining)} away from free domestic shipping.`;
    } else {
      message.textContent = 'You unlocked free domestic shipping!';
    }
  }


  /**
   * Update the Complete Kit Savings progress nudge (percentage tiers, currency-agnostic).
   * Tier 1: 2 items = 10% off. Tier 2: 3+ items = 15% off. Auto-applied at checkout.
   * Uses total cart item quantity as a motivational signal; Shopify calculates the
   * real discount on eligible items at checkout.
   */
  updateKitSavingsProgress(totalItems) {
    const container = document.getElementById('kingz-cart-kit-savings');
    const message = document.getElementById('kingz-cart-kit-savings-message');
    const bar = document.getElementById('kingz-cart-kit-savings-bar');
    if (!container || !message || !bar) return;

    const t1 = parseInt(container.dataset.tier1Qty || '2', 10);
    const p1 = parseInt(container.dataset.tier1Pct || '10', 10);
    const t2 = parseInt(container.dataset.tier2Qty || '3', 10);
    const p2 = parseInt(container.dataset.tier2Pct || '15', 10);
    const qty = totalItems || 0;

    const progress = Math.min((qty / t2) * 100, 100);
    bar.style.width = `${progress}%`;

    container.classList.remove('is-tier1', 'is-tier2');
    if (qty <= 0) {
      message.innerHTML = `Add <strong>${t1}</strong> items to save <strong>${p1}%</strong> — or <strong>${t2}+</strong> to save <strong>${p2}%</strong>.`;
    } else if (qty < t1) {
      const need = t1 - qty;
      message.innerHTML = `Add <strong>${need}</strong> more ${need === 1 ? 'item' : 'items'} to unlock <strong>${p1}% Complete Kit Savings</strong>.`;
    } else if (qty < t2) {
      const need = t2 - qty;
      message.innerHTML = `🎉 You've unlocked <strong>${p1}% off</strong>! Add <strong>${need}</strong> more to save <strong>${p2}%</strong>.`;
      container.classList.add('is-tier1');
    } else {
      message.innerHTML = `🎉 You've unlocked the full <strong>${p2}% Complete Kit Savings</strong>!`;
      container.classList.add('is-tier2');
    }
  }

  /**
   * Update subtotal display
   */
  updateSubtotal() {
    if (!this.cart || !this.subtotal) return;

    const total = this.cart.items_subtotal_price || 0;
    this.subtotal.textContent = this.formatMoney(total);
  }

  /**
   * Set item updating state
   */
  setItemUpdating(lineKey, isUpdating) {
    const item = this.itemsContainer.querySelector(`[data-line-item-key="${lineKey}"]`);
    if (!item) return;

    const btns = item.querySelectorAll('button, input');

    if (isUpdating) {
      item.classList.add('is-updating');
      btns.forEach((btn) => btn.disabled = true);
    } else {
      item.classList.remove('is-updating');
      btns.forEach((btn) => btn.disabled = false);
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    console.error(message);
    // You can add a toast notification here if desired
  }

  /**
   * Handle keyboard events
   */
  handleKeydown(event) {
    // Close on Escape
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      return;
    }

    // Trap focus within drawer when open
    if (!this.isOpen || !this.drawer) return;

    const focusableElements = this.drawer.querySelectorAll(
      'button, [href], input, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.key === 'Tab') {
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  }

  /**
   * Format price in store currency
   */
  formatMoney(cents) {
    if (!cents) return '$0.00';

    const dollars = (cents / 100).toFixed(2);
    const parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

/**
 * Initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.kingzSlideCart = new KingzSlideCart();
  });
} else {
  window.kingzSlideCart = new KingzSlideCart();
}

/**
 * Helper function for pages to trigger cart update
 * Usage: kingzSlideCart.fetchCart()
 */
window.updateKingzCart = () => {
  if (window.kingzSlideCart) {
    window.kingzSlideCart.fetchCart();
  }
};
